import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import { keys } from "@/api/queryKeys";
import type { OrgTermsStateName } from "@/api/types";
import type { LegalChanges, LegalDoc, LegalLang } from "@/features/legal/docs";
import { clearOrgTermsRequired } from "@/features/legal/orgTermsGate";
import { clearTermsRequired } from "@/features/legal/termsGate";
import type { SignupConversion, SignupTrackingConfig } from "@/lib/signupTracking";

/** The documents a person accepts (the DPA and the sub-processor list
 *  are annexes to the Terms and accepted with them). */
export type AcceptedLegalDoc = "terms" | "privacy";

/** ``POST /api/legal/accept-signup`` and ``/accept``: both boxes, and the
 *  UI language the page was read in (recorded with the acceptance). */
export type LegalAcceptBody = {
  accept_terms: boolean;
  accept_privacy: boolean;
  ui_language: string;
  /** The sign-up page's optional "Send me product news and tips" box;
   *  accept-signup only. */
  marketing_opt_in?: boolean;
};

/** ``GET /api/legal/pending-signup``: who is signing up. 400
 *  ``signup_expired`` when the sealed cookie is missing or too old. */
export type PendingSignup = {
  display_name: string;
  email: string;
  /** The conversion tracking this page loads (FW-39); null where the
   *  deployment doesn't track. */
  tracking?: SignupTrackingConfig | null;
};

/** ``POST /api/legal/accept-signup``: where to go, and for a self sign-up
 *  on a tracking deployment the ``sign_up`` conversion to push. */
export type AcceptSignupResult = { redirect: string; conversion?: SignupConversion };

export const SIGNUP_EXPIRED = "signup_expired";

/** Whether this deployment publishes the legal documents
 *  (``RW_LEGAL_TERMS_ENABLED``; the cloud service does, a self-hosted or
 *  internal one does not): ``GET /api/legal`` answers 404 when it
 *  doesn't. ``undefined`` while unknown, so nothing flashes in. */
export function useLegalPublished(): boolean | undefined {
  const index = useQuery({
    queryKey: ["legal", "index"],
    queryFn: async () => (await api.get<Record<string, unknown>>("/legal")).data,
    retry: false,
    staleTime: Infinity,
  });
  if (index.isSuccess) return true;
  if (index.isError) return false;
  return undefined;
}

export function usePendingSignup() {
  return useQuery({
    queryKey: ["legal", "pending-signup"],
    queryFn: async () => (await api.get<PendingSignup>("/legal/pending-signup")).data,
    retry: false,
    staleTime: Infinity,
  });
}

/** A first sign-in accepts: the backend creates the account, signs in and
 *  answers with the page to go to. */
export function useAcceptSignup() {
  return useMutation({
    mutationFn: async (body: LegalAcceptBody) =>
      (await api.post<AcceptSignupResult>("/legal/accept-signup", body)).data,
  });
}

/** The person declined: the backend forgets the pending sign-in. */
export function useCancelSignup() {
  return useMutation({
    mutationFn: async () => {
      await api.post("/legal/cancel-signup");
    },
  });
}

/** A signed-in user accepts the current versions; the gate lifts once
 *  ``/auth/me`` reports nothing left to accept. */
export function useAcceptLegal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: LegalAcceptBody) => {
      await api.post("/legal/accept", body);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.me });
      clearTermsRequired();
    },
  });
}

/** ``GET /api/legal``: each document's latest version (and the one in
 *  effect). 404 where the deployment doesn't publish the documents. */
export type LegalIndexEntry = {
  version: number;
  effective: string;
  published_at: string;
  effective_at: string;
  change_class: string;
  in_effect_version: number;
  title_sv: string;
  title_en: string;
};
export type LegalIndex = Record<string, LegalIndexEntry>;

/** The index, shared with :func:`useLegalPublished` (same key). */
export function useLegalIndex(enabled = true) {
  return useQuery({
    queryKey: ["legal", "index"],
    queryFn: async () => (await api.get<LegalIndex>("/legal")).data,
    retry: false,
    staleTime: Infinity,
    enabled,
  });
}

/** ``GET /api/orgs/{slug}/legal`` (spec §8.3). */
export type OrgLegal = {
  state: OrgTermsStateName;
  deadline: string | null;
  /** The deadline's date in Sweden, ``YYYY-MM-DD``. */
  deadline_date?: string | null;
  latest: { terms: number; dpa: number };
  required: { terms: number; dpa: number };
  accepted: Record<string, { version: number; at: string; by: string | null; source: string }>;
  admins: string[];
  has_admin: boolean;
};

export const orgLegalKey = (slug: string | null) => ["legal", "org", slug] as const;

export function useOrgLegal(slug: string | null) {
  return useQuery({
    queryKey: orgLegalKey(slug),
    queryFn: async () => (await api.get<OrgLegal>(`/orgs/${slug}/legal`)).data,
    enabled: !!slug,
    retry: false,
  });
}

/** 409 from accept: a newer version was published after the dialog
 *  loaded; the dialog reloads and shows it (spec §8.6). */
export const LEGAL_VERSION_CHANGED = "legal_version_changed";

export function isLegalVersionChanged(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  return response?.status === 409 && response.data?.detail === LEGAL_VERSION_CHANGED;
}

/** An org admin accepts the versions the dialog showed. On a 409
 *  ``legal_version_changed`` the org's state is refetched so the dialog
 *  shows the new versions before anyone accepts again. */
export function useAcceptOrgLegal(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { terms_version: number; dpa_version: number; ui_language: string }) =>
      (await api.post<OrgLegal>(`/orgs/${slug}/legal/accept`, {
        ...body,
        accept_terms: true,
        accept_dpa: true,
      })).data,
    onSuccess: (data) => {
      clearOrgTermsRequired();
      qc.setQueryData(orgLegalKey(slug), data);
      void qc.invalidateQueries({ queryKey: keys.me });
    },
    onError: (error) => {
      if (isLegalVersionChanged(error)) {
        void qc.invalidateQueries({ queryKey: orgLegalKey(slug) });
        void qc.invalidateQueries({ queryKey: ["legal", "index"] });
        // "What changed" is read per version.
        void qc.invalidateQueries({ queryKey: ["legal", "versions"] });
        void qc.invalidateQueries({ queryKey: ["legal", "changes"] });
      }
    },
  });
}

/** "Got it" on an information notice: ``acknowledge`` rows. */
export function useAcknowledgeLegal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { documents: Record<string, number>; ui_language: string }) => {
      await api.post("/legal/acknowledge", body);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.me }),
  });
}

/** ``GET /api/legal/{doc}/changes``; ``from``/``to`` null lets the
 *  backend pick (the latest version and the one before it). */
export function useLegalChanges(
  doc: LegalDoc,
  from: number | null,
  to: number | null,
  lang: LegalLang,
  enabled = true,
) {
  return useQuery({
    queryKey: ["legal", "changes", doc, from, to, lang],
    queryFn: async () =>
      (
        await api.get<LegalChanges>(`/legal/${doc}/changes`, {
          params: { lang, ...(from ? { from } : {}), ...(to ? { to } : {}) },
        })
      ).data,
    staleTime: 5 * 60_000,
    retry: false,
    enabled,
  });
}
